<template>
    <div class="rtp-timeline">
        <div class="timeline-header">
            <h5>
                <i class="bi bi-clock-history"></i>
                RTP Journey Timeline
            </h5>
            <div class="timeline-summary" v-if="totalDays !== null">
                <span class="summary-item">
                    <i class="bi bi-calendar-range"></i>
                    {{ totalDays }} days total
                </span>
                <span v-if="regressionCount > 0" class="summary-item regression">
                    <i class="bi bi-exclamation-triangle"></i>
                    {{ regressionCount }} setback{{ regressionCount > 1 ? 's' : '' }}
                </span>
            </div>
        </div>

        <!-- Timeline -->
        <div class="timeline-container">
            <!-- Injury Start -->
            <div class="timeline-item injury-start">
                <div class="timeline-marker">
                    <i class="bi bi-bandaid"></i>
                </div>
                <div class="timeline-content">
                    <div class="timeline-date">{{ formatDate(injuryDate) }}</div>
                    <div class="timeline-title">Injury Reported</div>
                    <div class="timeline-description">{{ injuryType }}</div>
                    <div v-if="injuryReportedBy && injuryReportedBy.name" class="timeline-meta">
                        <i class="bi bi-person-badge"></i>
                        <span>Reported by: {{ injuryReportedBy.name }}</span>
                        <span v-if="injuryReportedBy.role" class="role-tag">{{ injuryReportedBy.role }}</span>
                    </div>
                </div>
            </div>

            <!-- RTP Started -->
            <div v-if="rtpStartDate" class="timeline-item rtp-start">
                <div class="timeline-marker">
                    <i class="bi bi-play-circle"></i>
                </div>
                <div class="timeline-content">
                    <div class="timeline-date">{{ formatDate(rtpStartDate) }}</div>
                    <div class="timeline-title">RTP Protocol Started</div>
                    <div v-if="maturityAtInjury" class="timeline-tag maturity"
                        :class="'maturity-' + maturityAtInjury.toLowerCase().replace('-', '')">
                        {{ maturityAtInjury }}
                    </div>
                </div>
            </div>

            <!-- Phase Entries -->
            <div v-for="(entry, index) in phaseHistory" :key="entry.id" class="timeline-item phase-entry"
                :class="{ 'regression': entry.is_regression, 'current': isCurrentPhase(entry, index) }">
                <div class="timeline-marker" :style="{ backgroundColor: getPhaseColor(entry.phase_number) }">
                    <i v-if="entry.is_regression" class="bi bi-arrow-counterclockwise"></i>
                    <span v-else>{{ entry.phase_number }}</span>
                </div>
                <div class="timeline-content">
                    <div class="timeline-date">{{ formatDate(entry.entered_date) }}</div>
                    <div class="timeline-title">
                        <span v-if="entry.is_regression" class="regression-label">Regression to </span>
                        Phase {{ entry.phase_number }}: {{ entry.phase_name }}
                    </div>

                    <!-- Phase Lead -->
                    <div v-if="entry.phase_lead_name" class="timeline-meta">
                        <i class="bi bi-person"></i>
                        <span>{{ entry.phase_lead_name }}</span>
                        <span v-if="entry.phase_lead_role" class="role-tag">{{ entry.phase_lead_role }}</span>
                    </div>

                    <!-- Restrictions -->
                    <div v-if="entry.restrictions" class="timeline-restrictions">
                        <i class="bi bi-shield-exclamation"></i>
                        {{ entry.restrictions }}
                    </div>

                    <!-- Adverse Reaction (for regressions) -->
                    <div v-if="entry.adverse_reaction" class="timeline-adverse">
                        <i class="bi bi-exclamation-triangle-fill"></i>
                        <strong>Adverse Reaction:</strong> {{ entry.adverse_reaction }}
                    </div>

                    <!-- Clearance Info (if phase completed) -->
                    <div v-if="entry.cleared_date" class="timeline-clearance">
                        <div class="clearance-header">
                            <i class="bi bi-check-circle"></i>
                            Cleared on {{ formatDate(entry.cleared_date) }}
                            <span v-if="entry.days_in_phase" class="days-badge">{{ entry.days_in_phase }} days</span>
                        </div>
                        <div v-if="entry.cleared_by_name" class="cleared-by">
                            By: {{ entry.cleared_by_name }}
                            <span v-if="entry.cleared_by_role" class="role-tag">{{ entry.cleared_by_role }}</span>
                        </div>
                        <div v-if="entry.sign_off_comments" class="sign-off-comments">
                            "{{ entry.sign_off_comments }}"
                        </div>
                    </div>

                    <!-- Current Phase Indicator -->
                    <div v-if="isCurrentPhase(entry, index) && !entry.cleared_date" class="current-indicator">
                        <span class="pulse"></span>
                        Currently in this phase
                        <span v-if="daysInCurrentPhase !== null" class="days-badge">{{ daysInCurrentPhase }} days</span>
                    </div>
                </div>
            </div>

            <!-- Clearance (if completed) -->
            <div v-if="isCompleted" class="timeline-item clearance-complete">
                <div class="timeline-marker">
                    <i class="bi bi-trophy"></i>
                </div>
                <div class="timeline-content">
                    <div class="timeline-date">{{ formatDate(clearanceDate) }}</div>
                    <div class="timeline-title">Full Clearance Granted</div>
                    <div v-if="clearedBy" class="timeline-meta">
                        <i class="bi bi-person-check"></i>
                        <span>{{ clearedBy }}</span>
                    </div>
                    <div class="timeline-description success">
                        Player cleared for full participation
                    </div>
                </div>
            </div>

            <!-- Target Date (if not completed) -->
            <div v-else-if="targetDate" class="timeline-item target-date">
                <div class="timeline-marker">
                    <i class="bi bi-flag"></i>
                </div>
                <div class="timeline-content">
                    <div class="timeline-date">{{ formatDate(targetDate) }}</div>
                    <div class="timeline-title">Target Return Date</div>
                    <div v-if="daysUntilTarget !== null" class="timeline-description">
                        <span v-if="daysUntilTarget > 0">{{ daysUntilTarget }} days remaining</span>
                        <span v-else-if="daysUntilTarget === 0">Target date is today</span>
                        <span v-else class="overdue">{{ Math.abs(daysUntilTarget) }} days overdue</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-if="!phaseHistory || phaseHistory.length === 0" class="empty-state">
            <i class="bi bi-hourglass"></i>
            <p>No RTP timeline data available</p>
        </div>
    </div>
</template>

<script>
import { RTP_PHASES } from "@/services/rtp-service.js";

export default {
    name: "RTPTimeline",
    props: {
        injuryDate: {
            type: String,
            default: null
        },
        injuryType: {
            type: String,
            default: ''
        },
        injuryReportedBy: {
            type: Object,
            default: null
        },
        rtpStartDate: {
            type: String,
            default: null
        },
        maturityAtInjury: {
            type: String,
            default: null
        },
        phaseHistory: {
            type: Array,
            default: () => []
        },
        currentPhase: {
            type: Number,
            default: null
        },
        daysInCurrentPhase: {
            type: Number,
            default: null
        },
        targetDate: {
            type: String,
            default: null
        },
        isCompleted: {
            type: Boolean,
            default: false
        },
        clearanceDate: {
            type: String,
            default: null
        },
        clearedBy: {
            type: String,
            default: null
        }
    },
    computed: {
        totalDays() {
            if (!this.rtpStartDate) return null;

            const start = new Date(this.rtpStartDate);
            const end = this.isCompleted && this.clearanceDate
                ? new Date(this.clearanceDate)
                : new Date();

            return Math.floor((end - start) / (1000 * 60 * 60 * 24));
        },
        regressionCount() {
            if (!this.phaseHistory) return 0;
            return this.phaseHistory.filter(p => p.is_regression).length;
        },
        daysUntilTarget() {
            if (!this.targetDate) return null;
            const target = new Date(this.targetDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return Math.floor((target - today) / (1000 * 60 * 60 * 24));
        }
    },
    methods: {
        formatDate(dateStr) {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        },
        getPhaseColor(phaseNumber) {
            return RTP_PHASES[phaseNumber]?.color || '#6c757d';
        },
        isCurrentPhase(entry, index) {
            // Current phase is the last entry that hasn't been cleared
            if (this.isCompleted) return false;
            return index === this.phaseHistory.length - 1 && !entry.cleared_date;
        }
    }
};
</script>

<style scoped>
.rtp-timeline {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 12px;
}

.timeline-header h5 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
}

.timeline-header h5 i {
    color: #6c757d;
}

.timeline-summary {
    display: flex;
    gap: 16px;
}

.summary-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #6c757d;
}

.summary-item.regression {
    color: #dc3545;
}

/* Timeline Container */
.timeline-container {
    position: relative;
    padding-left: 30px;
}

.timeline-container::before {
    content: '';
    position: absolute;
    left: 14px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e9ecef;
}

/* Timeline Item */
.timeline-item {
    position: relative;
    padding-bottom: 24px;
    padding-left: 24px;
}

.timeline-item:last-child {
    padding-bottom: 0;
}

.timeline-marker {
    position: absolute;
    left: -16px;
    top: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #6c757d;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 14px;
    font-weight: 600;
    z-index: 1;
}

.timeline-marker i {
    font-size: 14px;
}

/* Item Types */
.timeline-item.injury-start .timeline-marker {
    background: #dc3545;
}

.timeline-item.rtp-start .timeline-marker {
    background: #007bff;
}

.timeline-item.regression .timeline-marker {
    background: #ffc107;
    color: #212529;
}

.timeline-item.clearance-complete .timeline-marker {
    background: #28a745;
}

.timeline-item.target-date .timeline-marker {
    background: #17a2b8;
}

.timeline-item.current::before {
    content: '';
    position: absolute;
    left: -20px;
    top: -4px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(0, 123, 255, 0.1);
    z-index: 0;
}

/* Timeline Content */
.timeline-content {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 12px 16px;
}

.timeline-item.regression .timeline-content {
    background: #fff3cd;
    border: 1px solid #ffc107;
}

.timeline-item.current .timeline-content {
    background: #e3f2fd;
    border: 1px solid #2196f3;
}

.timeline-item.clearance-complete .timeline-content {
    background: #d4edda;
    border: 1px solid #28a745;
}

.timeline-date {
    font-size: 12px;
    color: #6c757d;
    margin-bottom: 4px;
}

.timeline-title {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 8px;
}

.regression-label {
    color: #dc3545;
}

.timeline-description {
    font-size: 13px;
    color: #495057;
}

.timeline-description.success {
    color: #28a745;
    font-weight: 500;
}

.timeline-description .overdue {
    color: #dc3545;
    font-weight: 500;
}

.timeline-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #6c757d;
    margin-bottom: 8px;
}

.timeline-meta i {
    font-size: 12px;
}

.role-tag {
    background: #e9ecef;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
}

.timeline-tag.maturity {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.maturity-prephv {
    background: #e0f7fa;
    color: #006064;
}

.maturity-circaphv {
    background: #fff3e0;
    color: #e65100;
}

.maturity-postphv {
    background: #e8f5e9;
    color: #1b5e20;
}

.timeline-restrictions {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: #856404;
    background: rgba(255, 193, 7, 0.2);
    padding: 8px 10px;
    border-radius: 6px;
    margin-top: 8px;
}

.timeline-adverse {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 12px;
    color: #721c24;
    background: #f8d7da;
    padding: 8px 10px;
    border-radius: 6px;
    margin-top: 8px;
}

.timeline-adverse i {
    color: #dc3545;
    margin-top: 2px;
}

.timeline-clearance {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #dee2e6;
}

.clearance-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #28a745;
    font-weight: 500;
    margin-bottom: 4px;
}

.clearance-header i {
    font-size: 14px;
}

.days-badge {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    margin-left: 4px;
}

.cleared-by {
    font-size: 12px;
    color: #6c757d;
    margin-bottom: 4px;
}

.sign-off-comments {
    font-size: 12px;
    color: #495057;
    font-style: italic;
    background: rgba(0, 0, 0, 0.03);
    padding: 8px 10px;
    border-radius: 6px;
    margin-top: 6px;
}

.current-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #0d6efd;
    font-weight: 500;
    margin-top: 8px;
}

.pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #0d6efd;
    animation: pulse 1.5s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.4);
    }

    70% {
        box-shadow: 0 0 0 8px rgba(13, 110, 253, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(13, 110, 253, 0);
    }
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
}

/* Responsive */
@media (max-width: 576px) {
    .timeline-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .timeline-summary {
        flex-direction: column;
        gap: 8px;
    }

    .timeline-container {
        padding-left: 24px;
    }

    .timeline-container::before {
        left: 11px;
    }

    .timeline-marker {
        width: 24px;
        height: 24px;
        font-size: 11px;
        left: -12px;
    }

    .timeline-item {
        padding-left: 16px;
    }

    .timeline-content {
        padding: 10px 12px;
    }
}
</style>
